import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { ApplicationDTO, ApplicationFilterDTO, FeatureDTO, FeatureFilterDTO, IntegrationSettingDTO, IntegrationSettingFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, DatePickers, IChipsSettingProps } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { Message } from '../message/message';
import { IntegrationSettingMaintenance } from './IntegrationSettingMaintenance';
import { IntegrationParameter } from '../integrationParameter/IntegrationParameter';

export const IntegrationSetting: React.FC = () => {

    const [integrationTypeList, setIntegrationTypeList] = useState<ValueListDTO[]>([]);
    const [flowList, setFlowList] = useState<ValueListDTO[]>([]);
    const [sourceApplicationList, setSourceApplicationList] = useState<ApplicationDTO[]>([]);
    const [sourceFeatureList, setSourceFeatureList] = useState<FeatureDTO[]>([]);
    const [targetApplicationList, setTargetApplicationList] = useState<ApplicationDTO[]>([]);
    const [targetFeatureList, setTargetFeatureList] = useState<FeatureDTO[]>([]);
    const [productList, setProductList] = useState<ValueListDTO[]>([]);
    const [departmentList, setDepartmentList] = useState<ValueListDTO[]>([]);
    const [integrationSettingList, setIntegrationSettingList] = useState<IntegrationSettingDTO[]>([]);
    const [integrationSettingFilterDTO, setIntegrationSettingFilterDTO] = useState<IntegrationSettingFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setIntegrationSettingFilterDTO({
            ...integrationSettingFilterDTO,
            name: '',
            flowId: '',
            integrationTypeId: '',
            sourceApplicationId: '',
            sourceFeatureId: '',
            targetApplicationId: '',
            targetFeatureId: '',
            productId: '',
            departmentId: '',
            integrationGroupId: '',
            notificationSettingId: '',
        } as IntegrationSettingFilterDTO);
    }, []);

    useEffect(() => {
        handleListGetAll(ListId.linksphere.integrationSetting.flowId, setFlowList);
        handleListGetAll(ListId.linksphere.integrationSetting.integrationTypeId, setIntegrationTypeList);
        handleApplicationGetAllAsync(setSourceApplicationList);
        handleApplicationGetAllAsync(setTargetApplicationList);
        handleIntegrationSettingGetAllViewAsync();
    }, [integrationSettingFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleIntegrationSettingGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleFeatureGetAllAsync(setSourceFeatureList, integrationSettingFilterDTO?.sourceApplicationId || '');
    }, [integrationSettingFilterDTO?.sourceApplicationId]);

    useEffect(() => {
        handleFeatureGetAllAsync(setTargetFeatureList, integrationSettingFilterDTO?.targetApplicationId || '');
    }, [integrationSettingFilterDTO?.targetApplicationId]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleIntegrationSettingGetAllViewAsync = useCallback(async () => {
        if (integrationSettingFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('linksphere.integrationSetting', integrationSettingFilterDTO, setActionExecuted, setLoading, setIntegrationSettingList, showMessage, true);
    }, [integrationSettingFilterDTO]);

    const handleApplicationGetAllAsync = useCallback(async (setList: any) => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleFeatureGetAllAsync = useCallback(async (setList: any, applicationId: string) => {
        const featureFilterDTO: FeatureFilterDTO = {
            name: '',
            applicationId: applicationId,
        };
        GenericService.GetAllAsync1('appsphere.feature', featureFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleClearFilterClick = () => {
        setIntegrationSettingFilterDTO({
            ...integrationSettingFilterDTO,
            name: '',
            flowId: '',
            integrationTypeId: '',
            sourceApplicationId: '',
            sourceFeatureId: '',
            targetApplicationId: '',
            targetFeatureId: '',
            productId: '',
            departmentId: '',
            integrationGroupId: '',
            notificationSettingId: '',
        } as IntegrationSettingFilterDTO);
    };

    function handleSearchClick() {
        handleIntegrationSettingGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New IntegrationSetting', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: integrationSettingList.length },
    ];

    const chipFlow : IChipsSettingProps[] = [
        { value: 'Outbound Integration', iconName: 'output_outlined', iconColor: 'success' },
        { value: 'Inbound Integration', iconName: 'input_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 6, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'department', label: 'Department', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'product', label: 'Product', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceApplication', label: 'Source Application', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'sourceFeature', label: 'Source Feature', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetApplication', label: 'Target Application', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'targetFeature', label: 'Target Feature', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'integrationType', label: 'Type', size: 3, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'flow', label: 'Flow', size: 3, type: 'text', visible: true, isChip: true, chipSettings: chipFlow, hasFieldLine: false, hasToolTip: true },
        
    ];
    
    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='INTEGRATION SETTING'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                placeholder={'Name'}
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.name}
                                obj={integrationSettingFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='flowId'
                                label='Flow'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.flowId}
                                obj={integrationSettingFilterDTO}
                                valueList={flowList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='integrationTypeId'
                                label='Integration Type'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.integrationTypeId}
                                obj={integrationSettingFilterDTO}
                                valueList={integrationTypeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceApplicationId'
                                label='Source Application'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.sourceApplicationId}
                                obj={integrationSettingFilterDTO}
                                valueList={sourceApplicationList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceFeatureId'
                                label='Source Feature'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.sourceFeatureId}
                                obj={integrationSettingFilterDTO}
                                valueList={sourceFeatureList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetApplicationId'
                                label='Target Application'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.targetApplicationId}
                                obj={integrationSettingFilterDTO}
                                valueList={targetApplicationList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='targetFeatureId'
                                label='Target Feature'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.targetFeatureId}
                                obj={integrationSettingFilterDTO}
                                valueList={targetFeatureList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='departmentId'
                                label='Department'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.departmentId}
                                obj={integrationSettingFilterDTO}
                                valueList={departmentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='productId'
                                label='Product'
                                setValue={setIntegrationSettingFilterDTO}
                                value={integrationSettingFilterDTO?.productId}
                                obj={integrationSettingFilterDTO}
                                valueList={productList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={integrationSettingList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'Integration Parameter'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <IntegrationParameter data={expadedData} />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <IntegrationSettingMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <IntegrationSettingMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'linksphere.integrationSetting'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};